/**
 * Versioned HTTP contract — `v1` — for the EVIDENCE ENGINE through the secure API gateway
 * (`services/idea-engine-api`), backing the evidence run DAG (IDEA-57 / ADR 0058) and the
 * stagewise explorer's persistence reads (ADR 0060).
 *
 * Same rationale as the other `./http/*` contracts (ADR 0001/0005/0023 §5): the api service
 * and the web app must parse the SAME shapes without importing one another. Three row shapes
 * (source / record / pack snapshot) plus the RPC-style POST bodies. The deep pack shape
 * (`EvidencePack` candidates, dimension assessments) is validated STRUCTURALLY here (object
 * maps) — the pure engine re-parses at its own boundary, exactly like `stages`/`verdict` on
 * the persistence contract. Breaking changes ship as `v2`, never by mutation.
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';
import { OwnerScopeSchema } from './persistence-v1.js';

// ── Shared field shapes ──────────────────────────────────────────────────────

const id = z.string().min(1).max(64);
/** A stage key / evidence channel as carried on the wire (validated against the known
 *  stage×channel map server-side — the gateway is authoritative, the schema stays lean). */
const stage = z.string().min(1).max(64);
const channel = z.string().min(1).max(64);

/** Lifecycle of a discovered source (ADR 0058 §7 — failure is data, never dropped). */
export const EvidenceSourceStatusSchema = z.enum(['found', 'fetched', 'blocked', 'failed', 'extracted']);
export type EvidenceSourceStatus = z.infer<typeof EvidenceSourceStatusSchema>;

// ── Persisted payloads (response shapes) ─────────────────────────────────────

/** Wire shape of an `evidence_sources` row (parsed on read by both sides). */
export const EvidenceSourcePayloadSchema = z.object({
  id,
  ownerId: id,
  ideaId: id,
  stage,
  channel,
  /** The run that discovered this source (diagnostic lineage). */
  runId: id.optional(),
  url: z.string().max(2000).optional(),
  title: z.string().max(500).optional(),
  /** v1 `SourceType` vocabulary (primary/secondary/…), kept as a bounded string on the wire. */
  sourceType: z.string().max(64).optional(),
  status: EvidenceSourceStatusSchema,
  errorKind: z.string().max(64).optional(),
  errorMessage: z.string().max(2000).optional(),
  /** Dedup key across re-discoveries (hash of the URL/content). */
  contentHash: z.string().max(128).optional(),
  /** Extracted page text, TRUNCATED at write (ADR 0058 §5) — never a raw body. */
  fetchedText: z.string().optional(),
  createdAt: z.number().int().nonnegative(),
});
export type EvidenceSourcePayload = z.infer<typeof EvidenceSourcePayloadSchema>;

/** Wire shape of an `evidence_records` row — one typed extraction (ADR 0059 §2). */
export const EvidenceRecordPayloadSchema = z.object({
  id,
  sourceId: id,
  ownerId: id,
  claim: z.string().min(1).max(2000),
  value: z.number(),
  unit: z.string().min(1).max(120),
  /** ISO date the underlying observation refers to (not the fetch date). */
  observedAt: z.string().max(64).optional(),
  segment: z.string().max(500).optional(),
  /** The model's supporting sentence. Verified against the source's `fetchedText` server-side. */
  quote: z.string().max(2000).optional(),
  /**
   * Verbatim grounding (IDEA-55): whether `quote` was located inside the source's fetched text, how,
   * and where. Computed server-side (never trusted from the model) so a record's quote provably came
   * from the source. Absent on legacy rows (string-only quote) + structured-connector records.
   */
  quoteVerified: z.boolean().optional(),
  quoteMethod: z.enum(['exact', 'normalized', 'unverified']).optional(),
  /** Half-open char offsets `[start, end)` into the source's `fetchedText`; present only when verified. */
  quoteStart: z.number().int().nonnegative().optional(),
  quoteEnd: z.number().int().nonnegative().optional(),
  createdAt: z.number().int().nonnegative(),
});
export type EvidenceRecordPayload = z.infer<typeof EvidenceRecordPayloadSchema>;

/** Wire shape of an `evidence_packs` row: the qualified-inputs snapshot + operator overrides. */
export const EvidencePackRowPayloadSchema = z.object({
  id,
  ownerId: id,
  ideaId: id,
  stage,
  channel,
  /** Contract version tag (e.g. `squadlab.evidence-filter.v2`) so old snapshots stay parseable. */
  schema: z.string().min(1).max(120),
  /** Structural guard: `{ question, candidates }` — the deep shape is the engine's boundary job. */
  pack: z.record(z.unknown()),
  /** Operator filter edits (`FilterOverrides`), applied non-destructively by the engine on read. */
  overrides: z.record(z.unknown()).optional(),
  updatedAt: z.number().int().nonnegative(),
});
export type EvidencePackRowPayload = z.infer<typeof EvidencePackRowPayloadSchema>;

// ── Operator overrides (bounded FilterOverrides mirror) ─────────────────────

/**
 * Loose wire mirror of the domain `FilterOverrides` (evidence-filter v1): thresholds + per-source
 * include/decision. Bounded and parsed (never trusted), but deliberately tolerant — the pure
 * engine is the authority on what an override means; the wire only keeps malformed JSON out.
 */
export const FilterOverridesWireSchema = z.object({
  thresholds: z
    .object({
      rejectFloor: z.number().min(0).max(1).optional(),
      highBand: z.number().min(0).max(1).optional(),
    })
    .optional(),
  sources: z
    .record(
      z.object({
        include: z.boolean().optional(),
        decision: z.enum(['use', 'use-cautiously', 'proxy-only', 'reject', 'auto']).optional(),
      }),
    )
    .optional(),
});
export type FilterOverridesWire = z.infer<typeof FilterOverridesWireSchema>;

// ── POST /v1/evidence/packs/get ──────────────────────────────────────────────

/** Request: read one idea×stage×channel pack snapshot (`data: null` = no pack yet — a normal state). */
export const GetEvidencePackRequestSchema = OwnerScopeSchema.extend({ ideaId: id, stage, channel });
export type GetEvidencePackRequest = z.infer<typeof GetEvidencePackRequestSchema>;
/** Response: the pack row, or `null` when discovery has not produced one yet. */
export const EvidencePackRowResponseSchema = apiEnvelope(EvidencePackRowPayloadSchema.nullable());
export type EvidencePackRowResponse = z.infer<typeof EvidencePackRowResponseSchema>;

// ── POST /v1/evidence/sources/list ───────────────────────────────────────────

/** Request: list one idea×stage×channel's discovered sources (all statuses — failure is data). */
export const ListEvidenceSourcesRequestSchema = OwnerScopeSchema.extend({ ideaId: id, stage, channel });
export type ListEvidenceSourcesRequest = z.infer<typeof ListEvidenceSourcesRequestSchema>;
export const ListEvidenceSourcesResponseSchema = apiEnvelope(z.array(EvidenceSourcePayloadSchema));
export type ListEvidenceSourcesResponse = z.infer<typeof ListEvidenceSourcesResponseSchema>;

// ── POST /v1/evidence/records/list ───────────────────────────────────────────

/** Request: list the typed records extracted from the named sources (bounded fan-in). */
export const ListEvidenceRecordsRequestSchema = OwnerScopeSchema.extend({
  sourceIds: z.array(id).min(1).max(100),
});
export type ListEvidenceRecordsRequest = z.infer<typeof ListEvidenceRecordsRequestSchema>;
export const ListEvidenceRecordsResponseSchema = apiEnvelope(z.array(EvidenceRecordPayloadSchema));
export type ListEvidenceRecordsResponse = z.infer<typeof ListEvidenceRecordsResponseSchema>;

// ── POST /v1/evidence/overrides/save ─────────────────────────────────────────

/** Request: persist the operator's filter overrides for one idea×stage×channel (upsert). */
export const SaveEvidenceOverridesRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  stage,
  channel,
  overrides: FilterOverridesWireSchema,
});
export type SaveEvidenceOverridesRequest = z.infer<typeof SaveEvidenceOverridesRequestSchema>;
// Response: `EvidencePackRowResponseSchema` (the pack row carrying the saved overrides).

// ── Worker-facing pipeline steps (IDEA-57 / ADR 0058) ───────────────────────
// The background worker's evidence executors call these; the SEAMS (search engine, page fetch,
// record extraction, quality rating) live gateway-side because secrets + egress belong to the
// gateway. Gated like the rest of /api; `ownerId` rides from the leased task's owner.

// ── POST /v1/evidence/search ─────────────────────────────────────────────────

/** Request: run the channel's queries and REGISTER the found sources (dedup by hash/url). */
export const EvidenceSearchRequestSchema = OwnerScopeSchema.extend({
  ideaId: id,
  stage,
  channel,
  /** The run performing the discovery (stamped onto the source rows for lineage). */
  runId: id.optional(),
  /** ISO country/region the idea targets — drives geography-aware structured connectors (ADR 0067). */
  geography: z.string().min(1).max(32).optional(),
  queries: z.array(z.string().min(1).max(400)).min(1).max(8),
});
export type EvidenceSearchRequest = z.infer<typeof EvidenceSearchRequestSchema>;
/** Response data: the registered sources; `warning` set when the per-run budget cap dropped hits. */
export const EvidenceSearchDataSchema = z.object({
  sources: z.array(EvidenceSourcePayloadSchema),
  warning: z.string().max(500).optional(),
});
export type EvidenceSearchData = z.infer<typeof EvidenceSearchDataSchema>;
export const EvidenceSearchResponseSchema = apiEnvelope(EvidenceSearchDataSchema);
export type EvidenceSearchResponse = z.infer<typeof EvidenceSearchResponseSchema>;

// ── POST /v1/evidence/fetch ──────────────────────────────────────────────────

/** Request: fetch one found source's page (guarded egress — ADR 0058 §5) and persist the outcome. */
export const EvidenceFetchRequestSchema = z.object({ sourceId: id });
export type EvidenceFetchRequest = z.infer<typeof EvidenceFetchRequestSchema>;
/** Response: the updated source row (`fetched`, or `blocked`/`failed` — failure is data). */
export const EvidenceSourceResponseSchema = apiEnvelope(EvidenceSourcePayloadSchema);
export type EvidenceSourceResponse = z.infer<typeof EvidenceSourceResponseSchema>;

// ── POST /v1/evidence/extract ────────────────────────────────────────────────

/** Request: extract typed records from one fetched source's text and persist them. */
export const EvidenceExtractRequestSchema = z.object({ sourceId: id });
export type EvidenceExtractRequest = z.infer<typeof EvidenceExtractRequestSchema>;
/**
 * Response data: the extracted records. `skipped: true` = the source was not extractable
 * (blocked/failed/already-extracted) and the step is a tolerated no-op, never an error.
 * `dropped` = how many records the model returned that failed the boundary parse (unparseable
 * figure — a range, a bare percentage, prose) and were set aside. Surfaced (not swallowed) so a
 * source that yields zero usable records still explains WHY instead of looking empty.
 */
export const EvidenceExtractDataSchema = z.object({
  sourceId: id,
  records: z.array(EvidenceRecordPayloadSchema),
  skipped: z.boolean().optional(),
  /** How many records the model returned that failed the boundary parse (unparseable figure) and
   *  were set aside — so a zero-record source is explainable, not a silent empty. The per-record
   *  reason is logged server-side; only the count is surfaced. */
  dropped: z.number().int().nonnegative().optional(),
});
export type EvidenceExtractData = z.infer<typeof EvidenceExtractDataSchema>;
export const EvidenceExtractResponseSchema = apiEnvelope(EvidenceExtractDataSchema);
export type EvidenceExtractResponse = z.infer<typeof EvidenceExtractResponseSchema>;

// ── POST /v1/evidence/embed ──────────────────────────────────────────────────

/** Request: chunk one fetched source's text, embed it, and store the chunk vectors (DEVOP-33). */
export const EvidenceEmbedRequestSchema = z.object({ sourceId: id });
export type EvidenceEmbedRequest = z.infer<typeof EvidenceEmbedRequestSchema>;
/**
 * Response data: how many chunks were embedded + stored. `skipped: true` = the source had no
 * fetched text to embed (blocked/failed/structured-connector) and the step is a tolerated no-op.
 */
export const EvidenceEmbedDataSchema = z.object({
  sourceId: id,
  chunks: z.number().int().nonnegative(),
  skipped: z.boolean().optional(),
});
export type EvidenceEmbedData = z.infer<typeof EvidenceEmbedDataSchema>;
export const EvidenceEmbedResponseSchema = apiEnvelope(EvidenceEmbedDataSchema);
export type EvidenceEmbedResponse = z.infer<typeof EvidenceEmbedResponseSchema>;

// ── POST /v1/evidence/qualify ────────────────────────────────────────────────

/** Request: rate every source's seven dimensions and persist the qualified-inputs pack snapshot. */
export const EvidenceQualifyRequestSchema = OwnerScopeSchema.extend({ ideaId: id, stage, channel });
export type EvidenceQualifyRequest = z.infer<typeof EvidenceQualifyRequestSchema>;
/** Response: the persisted pack row (non-null — qualify always snapshots, even an empty set). */
export const EvidenceQualifyResponseSchema = apiEnvelope(EvidencePackRowPayloadSchema);
export type EvidenceQualifyResponse = z.infer<typeof EvidenceQualifyResponseSchema>;
